<template>
  <!-- 설치 장소 토큰 회전 — 입력만 받는다. 확인창(두 갈래 문구)과 호출은 부모가 한다.
       실패(유예 진행 중 409 포함)는 error 로 받아 모달 상단에 보이고 모달은 닫지 않는다. -->
  <a-modal :open="open" :footer="null" :mask-closable="false" width="480px" @cancel="emit('close')">
    <template #title>
      <div class="modal-title">토큰 회전</div>
    </template>

    <div class="modal-content">
      <div class="pbx-site-rotate">
        <a-alert v-if="error" type="error" show-icon :message="error" />

        <p class="pbx-site-rotate__desc">
          새 프로비저닝 URL 을 만듭니다. 새 URL 은 회전 직후 한 번만 보입니다.
        </p>

        <label class="pbx-site-rotate__field">
          <span class="pbx-site-rotate__label">유예 시간(시간)</span>
          <a-input-number
            v-model:value="graceHours"
            class="pbx-site-rotate__number"
            :min="1"
            :max="GRACE_HOURS_MAX"
            :precision="0"
            :disabled="revoke"
          />
          <span class="pbx-site-rotate__hint">이 시간 동안 기존 URL 도 유효합니다. 그 안에 전화기들이 새 URL 로 옮겨갑니다.</span>
        </label>

        <div class="pbx-site-rotate__field" :class="{ 'is-danger': revoke }">
          <a-checkbox v-model:checked="revoke">
            <span class="pbx-site-rotate__danger-label">기존 URL 즉시 무효(오프보딩용)</span>
          </a-checkbox>
        </div>
      </div>

      <div class="modal-footer">
        <a-button @click="emit('close')">취소</a-button>
        <a-button
          type="primary"
          class="yellow-button"
          :danger="revoke"
          :disabled="!canSubmit"
          :loading="submitting"
          @click="submit"
        >
          {{ revoke ? '즉시 무효화' : '회전' }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

/**
 * 유예 기본 72시간(결정 15). `revoke_previous` 는 오프보딩 스위치 — 켜면 유예가 의미 없어
 * 입력을 잠근다(값은 그대로 보낸다).
 * 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "화면 명세 › 설치 장소" 토큰
 */
const PBX_SITE_GRACE_HOURS_DEFAULT = 72;
// 30일 — 서버 상한이 정해지면 맞춘다(초과는 서버가 VALIDATION_ERROR 로 거절)
const GRACE_HOURS_MAX = 24 * 30;

const props = defineProps({
  open: { type: Boolean, required: true },
  submitting: { type: Boolean, required: true },
  error: { type: String, default: null },
});

const emit = defineEmits(['close', 'request']);

const graceHours = ref(PBX_SITE_GRACE_HOURS_DEFAULT);
const revoke = ref(false);

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    graceHours.value = PBX_SITE_GRACE_HOURS_DEFAULT;
    revoke.value = false;
  },
  { immediate: true }
);

const canSubmit = computed(() => !props.submitting && (revoke.value || (graceHours.value ?? 0) >= 1));

function submit() {
  if (!canSubmit.value) return;
  emit('request', {
    grace_hours: graceHours.value ?? PBX_SITE_GRACE_HOURS_DEFAULT,
    revoke_previous: revoke.value,
  });
}
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 18px;
  font-weight: 700;
}

// 전역 Bootstrap 의 .modal-content 규칙(테두리·둥근 모서리·flex)을 끄고 여백을 준다
.modal-content {
  display: block;
  padding: 4px 24px 0;
  border: none;
  border-radius: 0;
  background: transparent;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 0 20px;
  border-top: 1px solid #e9ecef;
  margin-top: 16px;
}

.pbx-site-rotate {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
  font-size: 13px;

  &__desc {
    margin: 0;
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.6;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &.is-danger {
      padding: 8px 10px;
      border: 1px solid #ffccc7;
      border-radius: 8px;
      background: #fff2f0;
    }
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #262626;
  }

  &__number {
    width: 160px;
  }

  &__hint {
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.5;
  }

  &__danger-label {
    color: #cf1322;
    font-weight: 600;
  }
}

/* 전역 정의가 없는 클래스라 쓰는 컴포넌트마다 들고 있어야 한다. */
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
